import { useState, useEffect } from 'react';
import DateFilterSelector from '../utils/DateFilterSelector';
import { Icon } from 'semantic-ui-react';
import DiscipleshipSummaries from './DiscipleshipSummaries';
import moment from 'src/services/moment_instance';

import { DiscipleshipLeadersCharts } from './DiscipleshipLeadersCharts';
import { DiscipleshipDisciplesCharts } from './DiscipleshipDisciplesCharts';
import { DiscipleshipOpenGroupsCharts } from './DiscipleshipOpenGroupsCharts';
import { DiscipleshipInactiveGroupsCharts } from './DiscipleshipInactiveGroupsCharts';
import { GenerationSelector } from '../utils/GenerationSelector';
import { getGroupGeneration, sortGenerationLabels } from '../utils/groupsTranslationMap';
import DateToSelector from '../utils/DateToSelector';

export const DiscipleshipRange = ({
  closedGroupsNotGrouped,
  openGroupsNotGrouped,
  kindOfBoard,
  fromRange,
  toRange,
  toCut,
  setFromRange,
  setToRange,
  setToCut,
}) => {
  const [selectedCategoryForOpenGroups, setSelectedCategoryForOpenGroups] = useState('Todos');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  useEffect(() => {
    if (kindOfBoard === 'range') {
      setFrom(fromRange);
      setTo(toRange);
    } else {
      setFrom('1900-01-01');
      setTo(toCut);
    }
  }, [kindOfBoard, fromRange, toRange, toCut]);

  const openGroups = openGroupsNotGrouped || [];
  const closedGroups = closedGroupsNotGrouped || [];

  const closedGroupsByActivityName = closedGroups.reduce((acc, group) => {
    const activityName = group.activityName;
    const translatedName = getGroupGeneration(activityName);

    if (!acc[translatedName]) {
      acc[translatedName] = [];
    }

    acc[translatedName].push(group);
    return acc;
  }, {});

  const openGrouped = openGroups.reduce((acc, group) => {
    const activityName = group.activityName;
    const translatedName = getGroupGeneration(activityName);

    if (!acc[translatedName]) {
      acc[translatedName] = [];
    }

    acc[translatedName].push(group);
    return acc;
  }, {});
  openGrouped['Todos'] = openGroups;

  const openGroupsByActivityName = openGrouped;

  const groupedByGroup = Object.entries(openGrouped).map(([key, value]) => ({
    name: key,
    value: value.length,
    percentage: ((value.length / (openGroups.length || 1)) * 100).toFixed(1),
    data: value,
  }));

  const originalSummaryGroups = groupedByGroup;

  const groupedByDisciples = Object.entries(openGrouped).map(([key, value]) => {
    const totalMembers = value.flatMap((group) => group.GroupMembers || []);

    return {
      name: key,
      value: totalMembers.length,
      percentage: (
        (totalMembers.length /
          (openGroups.reduce((sum, g) => sum + (g.GroupMembers?.length || 0), 0) || 1)) *
        100
      ).toFixed(1),
      data: totalMembers,
    };
  });

  const originalSummaryDisciples = groupedByDisciples;

  const groupedByLeaders = Object.entries(openGrouped).map(([key, value]) => {
    const totalLeaders = value.flatMap((group) => {
      const leaders = [];
      if (group.maleLeader) {
        leaders.push({ ...group.maleLeader, groupCreatedAt: group.createdAt });
      }
      if (group.femaleLeader) {
        leaders.push({ ...group.femaleLeader, groupCreatedAt: group.createdAt });
      }
      return leaders;
    }, []);
    return {
      name: key,
      value: totalLeaders.length,
      percentage: (
        (totalLeaders.length /
          (openGroups.reduce(
            (sum, g) => sum + (g.maleLeader ? 1 : 0) + (g.femaleLeader ? 1 : 0),
            0
          ) || 1)) *
        100
      ).toFixed(1),
      data: totalLeaders,
    };
  });

  const originalSummaryLeaders = groupedByLeaders;

  const possibleCategories = sortGenerationLabels(Object.keys(openGrouped));

  const changeCategory = (event) => {
    setSelectedCategoryForOpenGroups(event.target.value);
  };

  const changeDates = ({ fromDate = '1900-01-01', toDate = '' }) => {
    if (kindOfBoard === 'range') {
      setFromRange(fromDate);
      setToRange(toDate);
    } else {
      setToCut(toDate);
    }
  };

  const withShareOfTotal = (items) => {
    const total = items.reduce((sum, item) => sum + (item.value || 0), 0) || 1;
    return items.map((item) => ({
      ...item,
      percentage: ((item.value / total) * 100).toFixed(1),
    }));
  };

  const handleDatesChangeForSummaries = () => {
    const filterByDate = (items, getDate) =>
      items.map((item) => {
        const filteredData = item.data.filter((entry) => isGroupInDateRange(getDate(entry)));
        return {
          ...item,
          value: filteredData.length,
          data: filteredData,
        };
      });

    const byGeneration = (items) =>
      sortGenerationLabels(items.filter((item) => item.name !== 'Todos').map((item) => item.name))
        .map((name) => items.find((item) => item.name === name))
        .filter(Boolean);

    return {
      summaryDisciplesForPieChart: withShareOfTotal(
        byGeneration(filterByDate(originalSummaryDisciples, (entry) => entry.createdAt))
      ),
      summaryGroupsForPieChart: withShareOfTotal(
        byGeneration(filterByDate(originalSummaryGroups, (entry) => entry.createdAt))
      ),
      summaryLeadersForPieChart: withShareOfTotal(
        byGeneration(filterByDate(originalSummaryLeaders, (entry) => entry.groupCreatedAt))
      ),
    };
  };

  // Funciones para mostrar los gráficos de líderes
  const handleLeadershipByType = () => {
    const groups = openGroupsByActivityName[selectedCategoryForOpenGroups] || [];

    // Agrupar usando reduce
    const counts = groups.reduce(
      (acc, group) => {
        if (!isGroupInDateRange(group.createdAt)) return acc;

        const isDiscipulado = group.kind === 'Grupo_Discipulado';
        const key = isDiscipulado ? 'discipulado' : 'lideres';
        acc[key].value += 1;
        if (group.maleLeader) {
          group.maleLeader.groupName = group.groupName;
          acc[key].data.push(group.maleLeader);
        }
        if (group.femaleLeader) {
          group.femaleLeader.groupName = group.groupName;
          acc[key].data.push(group.femaleLeader);
        }
        return acc;
      },
      {
        discipulado: { name: 'Grupo de discipulado', value: 0, data: [] },
        lideres: { name: 'Equipo de Líderes', value: 0, data: [] },
      }
    );

    // Clasificación de líderes
    const Leader = counts.discipulado.data;
    const trainer = counts.lideres.data;
    const totalLeaders = Leader.length + trainer.length;

    // Generar datos para el gráfico
    const segmentedByLeadershipType = [
      { name: 'Líder de grupo de discipulado', data: Leader },
      { name: 'Entrenador', data: trainer },
    ].map(({ name, data }) => ({
      name,
      value: data.length,
      percentage: totalLeaders ? ((data.length / totalLeaders) * 100).toFixed(1) : '0',
      data,
    }));
    return segmentedByLeadershipType;
  };

  const handleLeadershipByGender = () => {
    const groups = openGroupsByActivityName[selectedCategoryForOpenGroups] || [];

    const genderCounts = groups.reduce((acc, group) => {
      if (!isGroupInDateRange(group.createdAt)) return acc;

      [group.maleLeader, group.femaleLeader].forEach((leader) => {
        if (leader) {
          if (!acc[leader.gender]) {
            acc[leader.gender] = { count: 0, leaders: [] };
          }
          acc[leader.gender].count += 1;
          acc[leader.gender].leaders.push(leader);
        }
      });
      return acc;
    }, {});

    const leaderCount = Object.values(genderCounts).reduce((sum, { count }) => sum + count, 0);
    const segmentedByGender = Object.entries(genderCounts).map(([gender, { count, leaders }]) => ({
      name: gender,
      value: count,
      percentage: ((count / leaderCount) * 100).toFixed(1),
      data: leaders,
    }));

    return segmentedByGender;
  };

  //Funciones para mostrar los gráficos de discípulos

  const handleDiscipleshipByLeadershipType = () => {
    const groups = openGroupsByActivityName[selectedCategoryForOpenGroups] || [];
    const totalGroups = groups.length;

    // Inicializar los contadores y los arrays correspondientes
    const counts = {
      discipulado: { name: 'Grupo de discipulado', value: 0, data: [] },
      lideres: { name: 'Equipo de Líderes', value: 0, data: [] },
    };

    groups.forEach((group) => {
      if (!isGroupInDateRange(group.createdAt)) return;

      if (group.kind === 'Grupo_Discipulado') {
        // Equipo de discipulado
        counts.discipulado.value += 1;
        counts.discipulado.data.push(group);
      } else {
        // Equipo de líderes
        counts.lideres.value += 1;
        counts.lideres.data.push(group);
      }
    });

    // Calcular el porcentaje y construir el resultado final
    const result = Object.values(counts).map((item) => ({
      name: item.name,
      value: item.value,
      percentage: ((item.value / totalGroups) * 100).toFixed(1),
      data: item.data,
    }));

    return result;
  };

  const handleDisciplesByGender = () => {
    const groups = openGroupsByActivityName[selectedCategoryForOpenGroups] || [];
    const memberGenderCounts = groups.reduce((acc, group) => {
      group.GroupMembers?.forEach((member) => {
        const gender = member.male?.gender || member.female?.gender || 'No especificado';
        if (isGroupInDateRange(member.createdAt)) {
          if (!acc[gender]) {
            acc[gender] = { count: 0, members: [] };
          }
          acc[gender].count += 1;
          acc[gender].members.push(member);
        }
      });
      return acc;
    }, {});

    const memberCount = Object.values(memberGenderCounts).reduce(
      (sum, { count }) => sum + count,
      0
    );

    const chartData = Object.entries(memberGenderCounts).map(([gender, { count, members }]) => ({
      name: gender,
      value: count,
      percentage: ((count / memberCount) * 100).toFixed(1),
      data: members,
    }));
    return chartData;
  };

  const handleDisciplesGrowth = () => {
    const groups = openGroupsByActivityName[selectedCategoryForOpenGroups] || [];
    const monthNamesShort = [
      'Ene',
      'Feb',
      'Mar',
      'Abr',
      'May',
      'Jun',
      'Jul',
      'Ago',
      'Sep',
      'Oct',
      'Nov',
      'Dic',
    ];
    const end = moment().startOf('month');
    const start = end.clone().subtract(4, 'years');
    const months = [];

    for (let cursor = start.clone(); !cursor.isAfter(end); cursor.add(1, 'month')) {
      months.push({
        key: cursor.format('YYYY-MM'),
        name: `${monthNamesShort[cursor.month()]} ${cursor.format('YY')}`,
        fullName: `${cursor.format('MMMM')} ${cursor.format('YYYY')}`,
        cant: 0,
        nuevos: 0,
      });
    }

    groups.forEach((group) => {
      const memberCount = group.GroupMembers?.length || 0;
      if (!memberCount) return;

      const createdKey = moment(group.createdAt).startOf('month').format('YYYY-MM');
      let index = 0;
      while (index < months.length && months[index].key < createdKey) {
        index += 1;
      }

      for (; index < months.length; index += 1) {
        months[index].cant += memberCount;
      }
    });

    months.forEach((month, index) => {
      month.nuevos = index === 0 ? month.cant : month.cant - months[index - 1].cant;
    });

    return months;
  };

  //Funciones para mostrar los gráficos de grupos abiertos
  const handleOpenGroupByGender = () => {
    const groups = selectedCategoryForOpenGroups
      ? openGroupsByActivityName[selectedCategoryForOpenGroups] || []
      : openGroupsByActivityName['Todos'] || [];
    const totalGroups = groups.length || 1;

    // Inicializar los contadores y los arrays correspondientes
    let counts = {
      hombres: { name: 'Hombres', value: 0, data: [] },
      mujeres: { name: 'Mujeres', value: 0, data: [] },
      mixtos: { name: 'Mixtos', value: 0, data: [] },
    };
    groups.forEach((group) => {
      if (!isGroupInDateRange(group.createdAt)) return;
      const prefix = group.activityName.split('_')[0];

      if (prefix === 'Hombres' || prefix === 'Niños' || prefix === 'Chicos') {
        counts.hombres.value += 1;
        counts.hombres.data.push(group);
      } else if (prefix === 'Mujeres' || prefix === 'Niñas' || prefix === 'Chicas') {
        counts.mujeres.value += 1;
        counts.mujeres.data.push(group);
      } else if (
        prefix === 'Mixto' ||
        prefix === 'Mixtos' ||
        prefix === 'Parejas' ||
        prefix === 'Youth'
      ) {
        counts.mixtos.value += 1;
        counts.mixtos.data.push(group);
      }
    });
    // Calcular el porcentaje y construir el resultado final
    const result = Object.values(counts).map((item) => ({
      name: item.name,
      value: item.value,
      percentage: ((item.value / totalGroups) * 100).toFixed(1),
      data: item.data,
    }));

    return result;
  };

  const handleOpenGroupsByModality = () => {
    const groups = openGroupsByActivityName[selectedCategoryForOpenGroups] || [];

    const statusCounts = groups.reduce((acc, group) => {
      if (!isGroupInDateRange(group.createdAt)) return acc; // Skip if not in date range
      const statusKey = group.online ? 'Online' : 'Presencial';
      if (!acc[statusKey]) {
        acc[statusKey] = { count: 0, objects: [] };
      }
      acc[statusKey].count += 1;
      acc[statusKey].objects.push(group);

      return acc;
    }, {});

    const total = Object.values(statusCounts).reduce((sum, count) => sum + count.count, 0);
    const chartData = Object.entries(statusCounts).map(([status, { count, objects }]) => ({
      name: status,
      value: count,
      percentage: ((count / total) * 100).toFixed(1),
      data: objects,
    }));

    return chartData;
  };

  const handleOpenGroupByStatusChange = () => {
    const groups = openGroupsByActivityName[selectedCategoryForOpenGroups] || [];

    const statusCounts = groups.reduce((acc, group) => {
      if (!isGroupInDateRange(group.createdAt)) return acc; // Skip if not in date range
      const isActive = group.status;
      if (isActive) {
        acc['Cerrados'] = (acc['Cerrados'] || 0) + 1;
      } else {
        acc['Abiertos'] = (acc['Abiertos'] || 0) + 1;
      }
      return acc;
    }, {});

    const totalGroups = Object.values(statusCounts).reduce((sum, count) => sum + count, 0);
    const chartData = Object.entries(statusCounts).map(([status, count]) => ({
      name: status,
      cantidad: count,
      percentage: ((count / totalGroups) * 100).toFixed(1),
    }));

    return chartData;
  };

  //Funciones para mostrar los gráficos de grupos cerrados
  const handleClosedGroupsByCategory = () => {
    // Normalizar la categoria a las generaciones actuales
    const filteredData = Object.entries(closedGroupsByActivityName)
      .filter(([key]) => possibleCategories.includes(key))
      .map(([key, value]) => {
        // Filter groups by date range
        const filteredGroups = value.filter((group) => {
          return isGroupInDateRange(group.updatedAt) && group.deleted;
        });

        return {
          name: key,
          value: filteredGroups.length,
          data: filteredGroups,
        };
      });

    // Paso 2: calcular el total y mapear con el porcentaje
    const total = filteredData.reduce((sum, item) => sum + item.value, 0);

    const chartData = filteredData.map((item) => ({
      ...item,
      percentage: ((item.value / total) * 100).toFixed(1),
    }));

    if (selectedCategoryForOpenGroups !== 'Todos') return [];

    return sortGenerationLabels(chartData.map((item) => item.name))
      .map((name) => chartData.find((item) => item.name === name))
      .filter(Boolean);
  };

  const handleClosedGroupsByGroupType = () => {
    // Normalizar la categoria a las generaciones actuales
    const selectedData =
      selectedCategoryForOpenGroups === 'Todos'
        ? closedGroups
        : closedGroupsByActivityName[selectedCategoryForOpenGroups] || [];
    const counts = {
      discipulado: { name: 'Grupo de discipulado', value: 0, data: [] },
      lideres: { name: 'Equipo de Líderes', value: 0, data: [] },
    };
    (selectedData || []).forEach((group) => {
      if (!isGroupInDateRange(group.createdAt)) return;
      if (group.kind === 'Grupo_Discipulado') {
        // Equipo de discipulado
        counts.discipulado.value += 1;
        counts.discipulado.data.push(group);
      } else {
        // Equipo de líderes
        counts.lideres.value += 1;
        counts.lideres.data.push(group);
      }
    });
    const Leader = { Value: counts.discipulado.value, Data: counts.discipulado.data };
    const trainer = { Value: counts.lideres.value, Data: counts.lideres.data };
    const totalLeaders = Leader.Value + trainer.Value;

    const LeaderTrainer = ['Líder de grupo de discipulado', 'Entrenador'].map((name, index) => {
      const result = index === 0 ? Leader : trainer;
      return {
        name,
        value: result.Value,
        percentage: ((result.Value / totalLeaders) * 100).toFixed(1),
        data: result.Data,
      };
    });
    return LeaderTrainer;
  };

  // Función para verificar si la fecha de creación del grupo está dentro del rango
  const isGroupInDateRange = (createdAt) => {
    const groupDate = moment(createdAt);
    const fromDate = from ? moment(from) : null;
    const toDate = to ? moment(to) : null;

    if (fromDate && groupDate.isBefore(fromDate, 'day')) return false;
    if (toDate && groupDate.isAfter(toDate, 'day')) return false;
    return true;
  };

  const summaryCharts = handleDatesChangeForSummaries();

  return (
    <div className="groupsContainer">
      {kindOfBoard === 'range' && (
        <DateFilterSelector to={to} from={from} onFilterChange={changeDates} />
      )}
      {kindOfBoard === 'cut' && <DateToSelector to={to} onFilterChange={changeDates} />}

      <SectionDivider title="Resúmenes generales" icon="chart pie" />
      <DiscipleshipSummaries
        summaryLeadersForPieChart={summaryCharts.summaryLeadersForPieChart}
        summaryDisciplesForPieChart={summaryCharts.summaryDisciplesForPieChart}
        summaryGroupsForPieChart={summaryCharts.summaryGroupsForPieChart}
      />

      <SectionDivider title="Detalle por generación" icon="chart bar" />
      <GenerationSelector
        possibleCategories={possibleCategories}
        changeCategory={changeCategory}
        value={selectedCategoryForOpenGroups}
      />

      <SectionDivider title="Líderes" icon="home" filterLabel={selectedCategoryForOpenGroups} />
      <DiscipleshipLeadersCharts
        leadersAmountByLeadershipTypeDataForPieChart={handleLeadershipByType()}
        leadersByGenderPieChartData={handleLeadershipByGender()}
      />

      <SectionDivider title="Discípulos" icon="child" filterLabel={selectedCategoryForOpenGroups} />
      <DiscipleshipDisciplesCharts
        disciplesByGenderPieChartData={handleDisciplesByGender()}
        disciplesGrowthDataForLineChart={handleDisciplesGrowth()}
      />

      <SectionDivider
        title="Grupos abiertos"
        icon="users"
        filterLabel={selectedCategoryForOpenGroups}
      />
      <DiscipleshipOpenGroupsCharts
        openGroupsDataByLeadershipTypeForPieChart={handleDiscipleshipByLeadershipType()}
        openGroupsDataByGenderForPieChart={handleOpenGroupByGender()}
        openGroupsDataByModalityForPieChart={handleOpenGroupsByModality()}
        openGroupsDataByStatusForBarChart={handleOpenGroupByStatusChange()}
      />

      <SectionDivider
        title="Grupos inactivos"
        icon="user cancel"
        filterLabel={selectedCategoryForOpenGroups}
      />
      <DiscipleshipInactiveGroupsCharts
        closedGroupsByCategoryPieChartData={handleClosedGroupsByCategory()}
        closedGroupsByGroupTypePieChartData={handleClosedGroupsByGroupType()}
        selectedCategoryForOpenGroups={selectedCategoryForOpenGroups}
        closedGroupsByActivityName={closedGroupsByActivityName}
      />
    </div>
  );
};

export const SectionDivider = ({ title, icon, filterLabel }) => (
  <div className="chart-section-header">
    <Icon name={icon} />
    <h3>{title}</h3>
    {filterLabel && <span className="chart-section-filter">{filterLabel}</span>}
  </div>
);
